import {useEffect, useState} from 'react';
import {useMutation} from '@apollo/client';
import {Button, Form, InputNumber, Switch, Tag} from 'antd';
import {InfoCircleOutlined} from '@ant-design/icons';
import {toast} from 'react-toastify';
import PropTypes from 'prop-types';

import {
  ENABLE_OR_DISABLE_SOURCE_CURRENCY,
  UPDATE_WALLET_CURRENCY_SETTINGS,
} from 'graphql/mutations/wallet';
import {currencyName, symbolFor} from 'utils/currencyFormatting';
import styles from './swapSettings.module.scss';

/**
 * Everything that governs swaps *out of* one currency, in one place: whether
 * the currency can be swapped at all, and the amounts a single swap must sit
 * between. These used to be two near-identical modals, which made it easy to
 * open the wrong one.
 */
const SwapSettings = ({data, closeModal, refetch}) => {
  const [form] = Form.useForm();
  const [isEnabled, setIsEnabled] = useState(!!data?.isEnabled);

  const currency = data?.currency;
  const symbol = symbolFor(currency);

  const [updatePlatformWalletCurrencySettings, {loading: savingLimits}] =
    useMutation(UPDATE_WALLET_CURRENCY_SETTINGS);
  const [enableOrDisableSourceCurrencyForSwap, {loading: savingStatus}] =
    useMutation(ENABLE_OR_DISABLE_SOURCE_CURRENCY);

  useEffect(() => {
    setIsEnabled(!!data?.isEnabled);
    form.setFieldsValue({
      minAmount: data?.minAmount,
      maxAmount: data?.maxAmount,
    });
  }, [data, form]);

  const statusChanged = isEnabled !== !!data?.isEnabled;

  const saveStatus = () =>
    enableOrDisableSourceCurrencyForSwap({
      variables: {currency, isEnabled},
    }).then(({data: response}) => {
      const result = response?.enableOrDisableSourceCurrencyForSwap;
      if (result?.__typename === 'Error') throw new Error(result.message);
      return result;
    });

  const saveLimits = values =>
    updatePlatformWalletCurrencySettings({
      variables: {
        input: {
          currency,
          swap: {
            maxSwapAmountInSourceCurrency: Number(values?.maxAmount),
            minSwapAmountInSourceCurrency: Number(values?.minAmount),
          },
        },
      },
    }).then(({data: response}) => {
      const result = response?.updatePlatformWalletCurrencySettings;
      if (result?.__typename === 'Error') throw new Error(result.message);
      return result;
    });

  const handleFinish = async values => {
    try {
      // Status first: if the currency is being turned off, the limits still
      // save, but the operator sees the more consequential change land first.
      if (statusChanged) await saveStatus();
      await saveLimits(values);
      toast.success(`${currency} swap settings updated`);
      refetch?.();
      closeModal();
    } catch (error) {
      toast.error(error?.message ?? 'Could not save the swap settings');
    }
  };

  const saving = savingLimits || savingStatus;

  return (
    <div className={styles.modal}>
      <div className={styles.header}>
        <h1 className={styles.title}>
          {currency} swap settings
          <Tag
            className={styles.statusTag}
            color={data?.isEnabled ? 'green' : 'red'}>
            {data?.isEnabled ? 'Enabled' : 'Disabled'}
          </Tag>
        </h1>
        <p className={styles.subtitle}>
          Controls swaps that start from {currency} — {currencyName(currency)}.
          Swaps <em>into</em> {currency} are governed by the other currency’s
          settings.
        </p>
      </div>

      <Form
        form={form}
        onFinish={handleFinish}
        className={styles.form}
        layout="vertical">
        <div className={styles.toggleCard}>
          <div className={styles.toggleText}>
            <span className={styles.toggleLabel}>
              Allow users to swap out of {currency}
            </span>
            <span className={styles.toggleHint}>
              Turn this off and {currency} stops being offered as a source
              currency. Existing pending swaps are not affected.
            </span>
          </div>
          <Switch
            checked={isEnabled}
            onChange={setIsEnabled}
            className={styles.switch}
          />
        </div>

        <div className={styles.limitsRow}>
          <Form.Item
            rules={[
              {required: true, message: 'Enter a minimum'},
              {
                async validator(_, value) {
                  if (value === undefined || value === null) return;
                  if (Number(value) >= 0) return;
                  throw new Error('Cannot be negative');
                },
              },
            ]}
            name="minAmount"
            className={styles.limitField}
            label="Minimum per swap">
            <InputNumber
              className={styles.input}
              min={0}
              prefix={symbol}
              formatter={value =>
                `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')
              }
              parser={value => value.replace(/\$\s?|(,*)/g, '')}
            />
          </Form.Item>
          <Form.Item
            dependencies={['minAmount']}
            rules={[
              {required: true, message: 'Enter a maximum'},
              ({getFieldValue}) => ({
                async validator(_, value) {
                  const min = getFieldValue('minAmount');
                  if (value == null || min == null) return;
                  if (Number(value) >= Number(min)) return;
                  throw new Error('The maximum must be at least the minimum');
                },
              }),
            ]}
            name="maxAmount"
            className={styles.limitField}
            label="Maximum per swap">
            <InputNumber
              className={styles.input}
              min={0}
              prefix={symbol}
              formatter={value =>
                `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')
              }
              parser={value => value.replace(/\$\s?|(,*)/g, '')}
            />
          </Form.Item>
        </div>

        <p className={styles.note}>
          <InfoCircleOutlined /> Both limits are read in {currency}, the
          currency being swapped away.
        </p>

        <div className={styles.footer}>
          <Button onClick={closeModal}>Cancel</Button>
          <Button
            type="primary"
            htmlType="submit"
            className={styles.primaryButton}
            loading={saving}>
            Save settings
          </Button>
        </div>
      </Form>
    </div>
  );
};

SwapSettings.propTypes = {
  data: PropTypes.object,
  closeModal: PropTypes.func,
  refetch: PropTypes.func,
};

export default SwapSettings;
